/**
 * Storefront Hooks - Universal Dynamic Content Delivery Hooks
 * 
 * These hooks are designed for easy integration into any storefront.
 * They automatically use the active store configuration from storefrontConfig.js
 * 
 * USAGE:
 * 1. Import the hook you need
 * 2. Use it in your component - it will auto-configure based on active store
 * 
 * HOOKS AVAILABLE:
 * - useAd: Fetches and displays a single ad for a slot
 * - useRecommendations: Fetches personalized product recommendations
 * - useVideoCarousel: Fetches video carousel content with 1:3 auction ratio
 * - useCDPTracker: Tracks user events for personalization
 * - useAdPreferences: Block vendors or ads
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getActiveStoreConfig,
  getActiveChannel,
  getActiveApiKey,
  getSlotId,
  isDebugMode,
  isTrackingEnabled,
  getThemeConfig,
  VIDEO_CAROUSEL_CONFIG
} from '../config/storefrontConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== useAd Hook ====================
/**
 * Hook for fetching and displaying a single ad
 * 
 * @param {Object} options
 * @param {string} options.slotType - Slot type from storefrontConfig (e.g., 'homeBanner')
 * @param {string} options.slotId - Direct slot ID (overrides slotType)
 * @param {string} options.userId - User ID for personalization
 * @param {string} options.userLocation - User location for geo-targeting
 * @param {Function} options.onAdLoad - Callback when ad loads
 * @param {Function} options.onAdClick - Callback when ad is clicked
 * @param {Function} options.onAdError - Callback on error
 */
export const useAd = ({
  slotType = null,
  slotId = null,
  userId = null,
  userLocation = null,
  onAdLoad = null,
  onAdClick = null,
  onAdError = null
} = {}) => {
  const [adData, setAdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const config = getActiveStoreConfig();
  const channel = getActiveChannel();
  const apiKey = getActiveApiKey();
  const debug = isDebugMode();
  
  // Resolve the slot ID
  const resolvedSlotId = slotId || getSlotId(slotType) || slotType;

  const fetchAd = useCallback(async () => {
    if (!resolvedSlotId) {
      setError('No slot ID provided');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        slot_id: resolvedSlotId,
        channel: channel
      });
      if (userId) params.append('user_id', userId);
      if (userLocation) params.append('user_location', userLocation);
      
      const headers = {};
      if (apiKey && channel !== 'shopify') {
        headers['X-API-Key'] = apiKey;
      }
      
      
      const response = await fetch(`${API_URL}/api/recommend/ad/serve?${params}`, { headers });
      const data = await response.json();
      
      if (data.success && data.has_ad) {
        setAdData(data);
        if (onAdLoad) onAdLoad(data);
        
      } else {
        setAdData(null);
        
      }
    } catch (err) {
      console.error('[AdHook] Fetch error:', err);
      setError(err.message);
      if (onAdError) onAdError(err);
    } finally {
      setLoading(false);
    }
  }, [resolvedSlotId, userId, userLocation, channel, apiKey, debug, onAdLoad, onAdError]);

  // Track ad click
  const trackClick = useCallback(async () => {
    if (!adData?.tracking_id || !isTrackingEnabled()) return;
    
    try {
      await fetch(`${API_URL}/api/recommend/ad/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracking_id: adData.tracking_id })
      });
      if (onAdClick) onAdClick(adData);
    } catch (err) {
      console.error('[AdHook] Click tracking error:', err);
    }
  }, [adData, onAdClick]);

  useEffect(() => {
    fetchAd();
  }, [fetchAd]);

  return {
    adData,
    loading,
    error,
    trackClick,
    refetch: fetchAd,
    hasAd: !!adData,
    config,
    theme: getThemeConfig()
  };
};


// ==================== useRecommendations Hook ====================
/**
 * Hook for fetching personalized product recommendations
 * 
 * @param {Object} options
 * @param {string} options.slotType - Slot type: 'homepage', 'category', 'search', 'product_detail', 'video_carousel'
 * @param {string} options.userId - User ID for personalization
 * @param {string} options.categoryId - Category ID for category pages
 * @param {string} options.location - User location
 * @param {number} options.limit - Max recommendations to fetch
 * @param {Function} options.onLoad - Callback when data loads
 */
export const useRecommendations = ({
  slotType = 'homepage',
  userId = null,
  categoryId = null,
  location = null,
  limit = 20,
  onLoad = null
} = {}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasSponsored, setHasSponsored] = useState(false);
  
  const channel = getActiveChannel();
  const apiKey = getActiveApiKey();
  const debug = isDebugMode();

  const fetchRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        slot_type: slotType,
        limit: limit.toString(),
        channel: channel
      });
      if (userId) params.append('user_id', userId);
      if (categoryId) params.append('category_id', categoryId);
      if (location) params.append('location', location);
      
      const headers = {};
      if (apiKey && channel !== 'shopify') {
        headers['X-API-Key'] = apiKey;
      }
      
      
      const response = await fetch(`${API_URL}/api/recommend/list?${params}`, { headers });
      const data = await response.json();
      
      if (data.success) {
        setRecommendations(data.recommendations || []);
        setHasSponsored(data.has_sponsored || false);
        if (onLoad) onLoad(data);
        
      } else {
        setError(data.message || 'Failed to load recommendations');
      }
    } catch (err) {
      console.error('[RecommendationHook] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [slotType, userId, categoryId, location, limit, channel, apiKey, debug, onLoad]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  return {
    recommendations,
    loading,
    error,
    hasSponsored,
    refetch: fetchRecommendations,
    isEmpty: recommendations.length === 0,
    theme: getThemeConfig()
  };
};


// ==================== useVideoCarousel Hook ====================
/**
 * Hook for video carousel with 1:3 auction ratio
 * 
 * @param {Object} options
 * @param {string} options.userId - User ID for personalization
 * @param {string} options.location - User location
 * @param {Function} options.onLoad - Callback when data loads
 */
export const useVideoCarousel = ({
  userId = null,
  location = null,
  onLoad = null
} = {}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const channel = getActiveChannel();
  const apiKey = getActiveApiKey();
  const debug = isDebugMode();

  const fetchCarouselContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        slot_type: 'video_carousel',
        limit: VIDEO_CAROUSEL_CONFIG.totalSlots.toString(),
        channel: channel
      });
      if (userId) params.append('user_id', userId);
      if (location) params.append('location', location);
      
      const headers = {};
      if (apiKey && channel !== 'shopify') {
        headers['X-API-Key'] = apiKey;
      }
      
      
      const response = await fetch(`${API_URL}/api/recommend/list?${params}`, { headers });
      const data = await response.json();
      
      if (data.success) {
        setItems(data.recommendations || []);
        if (onLoad) onLoad(data);
        
      } else {
        setError(data.message || 'Failed to load carousel');
      }
    } catch (err) {
      console.error('[VideoCarouselHook] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, location, channel, apiKey, debug, onLoad]);

  // Track click on sponsored item
  const trackClick = useCallback(async (item) => {
    if (!item?.is_sponsored || !item?.tracking_id || !isTrackingEnabled()) return;
    
    try {
      await fetch(`${API_URL}/api/recommend/ad/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracking_id: item.tracking_id })
      });
    } catch (err) {
      console.error('[VideoCarouselHook] Click tracking error:', err);
    }
  }, []);

  useEffect(() => {
    fetchCarouselContent();
  }, [fetchCarouselContent]);

  return {
    items,
    loading,
    error,
    activeIndex,
    setActiveIndex,
    trackClick,
    refetch: fetchCarouselContent,
    isEmpty: items.length === 0,
    config: VIDEO_CAROUSEL_CONFIG,
    theme: getThemeConfig()
  };
};


// ==================== useCDPTracker Hook ====================
/**
 * Hook for tracking user events to enrich CDP profile
 */
export const useCDPTracker = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const debug = isDebugMode();
  const trackingEnabled = isTrackingEnabled();

  const trackEvent = useCallback(async (eventType, eventData = {}) => {
    if (!userId || !trackingEnabled) {
      
      return false;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/recommend/cdp/enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          event_type: eventType,
          event_data: eventData
        })
      });
      
      const data = await response.json();
      
      
      return data.success;
    } catch (err) {
      console.error('[CDPTracker] Tracking error:', err);
      return false;
    }
  }, [userId, debug, trackingEnabled]);

  // Convenience methods for common events
  const trackProductView = useCallback((productId, categoryId, vendorId) => {
    return trackEvent('view_product', { product_id: productId, category_id: categoryId, vendor_id: vendorId });
  }, [trackEvent]);

  const trackSearch = useCallback((query, categoryId, resultsCount) => {
    return trackEvent('search', { query, category_id: categoryId, results_count: resultsCount });
  }, [trackEvent]);

  const trackPurchase = useCallback((categoryId, amount) => {
    return trackEvent('purchase', { category_id: categoryId, amount });
  }, [trackEvent]);

  const trackReview = useCallback((vendorId, rating) => {
    return trackEvent('review', { vendor_id: vendorId, rating });
  }, [trackEvent]);

  return {
    trackEvent,
    trackProductView,
    trackSearch,
    trackPurchase,
    trackReview,
    isEnabled: trackingEnabled && !!userId
  };
};


// ==================== useAdPreferences Hook ====================
/**
 * Hook for managing ad preferences (blocking vendors/ads)
 */
export const useAdPreferences = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const debug = isDebugMode();

  const blockVendor = useCallback(async (vendorId) => {
    if (!userId) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/recommend/cdp/enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          event_type: 'block_vendor',
          event_data: { vendor_id: vendorId }
        })
      });
      
      const data = await response.json();
      
      return data.success;
    } catch (err) {
      console.error('[AdPreferences] Block vendor error:', err);
      return false;
    }
  }, [userId, debug]);

  const blockAd = useCallback(async (adId) => {
    if (!userId) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/recommend/cdp/enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          event_type: 'block_ad',
          event_data: { ad_id: adId }
        })
      });
      
      const data = await response.json();
      
      return data.success;
    } catch (err) {
      console.error('[AdPreferences] Block ad error:', err);
      return false;
    }
  }, [userId, debug]);

  return {
    blockVendor,
    blockAd,
    isAuthenticated: !!userId
  };
};


// ==================== useCDPProfile Hook ====================
/**
 * Hook for fetching user's CDP profile
 */
export const useCDPProfile = (userId = null) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  
  const resolvedUserId = userId || user?.id;

  const fetchProfile = useCallback(async () => {
    if (!resolvedUserId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/recommend/cdp/profile/${resolvedUserId}`);
      const data = await response.json();
      
      if (data.success) {
        setProfile(data.profile);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [resolvedUserId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    refetch: fetchProfile
  };
};


export default {
  useAd,
  useRecommendations,
  useVideoCarousel,
  useCDPTracker,
  useAdPreferences,
  useCDPProfile
};
